// Sektör paketleri DR1/DR2/DR3 — Paket / Katalog sekmelerinin TEK seçim kümesi (saf mantık).
// Paket = ön seçim: işaretli paketlerin kalemleri (kırılım seçici kuralıyla — toplam → grup +
// üyeler, üye → grubu) birleşime girer; Katalog sekmesindeki elle değişiklikler `manualAdded` /
// `manualRemoved` olarak tutulur ve paket aç/kapa sonrası korunur. Seçim her zaman türetilir:
//   selected = (paket birleşimi \ manualRemoved) ∪ manualAdded   (sette olanlar hariç)
// `checked` sunucuya `packageIds` olarak gider (yalnız iz / varsayılan / deprecated denetimi —
// sunucu birleşim YAPMAZ); üyeliğin tek kaynağı `selected` (fork `includeMetricIds`, bulk `metricIds`).

import { parseFormula, type KpiMetric, type KpiPackage } from "@mahir/shared";

export type SelectionMetric = Pick<
  KpiMetric,
  "id" | "parentId" | "nodeKind" | "code" | "defaultFormulas"
>;

export interface PackageSelectionState {
  /** işaretli paketler (işaretlenme sırası) → gövdede `packageIds` */
  checked: readonly string[];
  /** Katalog sekmesinde paketlerin dışında elle eklenenler */
  manualAdded: ReadonlySet<string>;
  /** Katalog sekmesinde paket kaleminden elle çıkarılanlar (paket işaretli kaldıkça dışarıda) */
  manualRemoved: ReadonlySet<string>;
  selected: ReadonlySet<string>;
}

export interface SelectionContext {
  byId: ReadonlyMap<string, SelectionMetric>;
  kids: ReadonlyMap<string, readonly SelectionMetric[]>;
  catalog: readonly SelectionMetric[];
  itemsByPackage: ReadonlyMap<string, readonly string[]>;
  /** Sete ekle: sette zaten olan metrikler (seçime girmez) */
  inSet: ReadonlySet<string>;
}

const NO_IDS: ReadonlySet<string> = new Set();

export function selectionContext(
  catalog: readonly SelectionMetric[],
  itemsByPackage: ReadonlyMap<string, readonly string[]>,
  inSet: ReadonlySet<string> = NO_IDS,
): SelectionContext {
  const byId = new Map(catalog.map((c) => [c.id, c]));
  const kids = new Map<string, SelectionMetric[]>();
  for (const c of catalog) {
    if (!c.parentId) continue;
    const list = kids.get(c.parentId) ?? [];
    list.push(c);
    kids.set(c.parentId, list);
  }
  return { byId, kids, catalog, itemsByPackage, inSet };
}

/** `metricId → paket kimlikleri` üyelik haritasını paket → kalem listesine çevirir. */
export function itemsByPackageFromMembership(
  membership: Readonly<Record<string, readonly string[]>>,
): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const [metricId, pkgIds] of Object.entries(membership)) {
    for (const p of pkgIds) {
      const list = out.get(p) ?? [];
      list.push(metricId);
      out.set(p, list);
    }
  }
  return out;
}

export function emptySelection(): PackageSelectionState {
  return { checked: [], manualAdded: NO_IDS, manualRemoved: NO_IDS, selected: NO_IDS };
}

/** Paketsiz açılış (DR2 eski davranış): verilen kimliklerin tümü elle seçili. */
export function catalogSelection(ids: Iterable<string>): PackageSelectionState {
  const all = new Set(ids);
  return { checked: [], manualAdded: all, manualRemoved: NO_IDS, selected: new Set(all) };
}

const isGroup = (m: SelectionMetric | undefined): boolean => m?.nodeKind === "breakdown_group";

/**
 * Kırılım seçici kuralının indeksli eşi (`togglePickWithBreakdown`, checked=true — testle birebir):
 * kalem; grupsa üyeleri; değilse kırılım grupları + üyeleri; üyeyse grubu. Sette olan atlanır.
 */
function addWithBreakdown(id: string, ctx: SelectionContext, out: Set<string>): void {
  const add = (mid: string): void => {
    if (!ctx.inSet.has(mid)) out.add(mid);
  };
  add(id);
  const node = ctx.byId.get(id);
  const groups = isGroup(node) ? [node!] : (ctx.kids.get(id) ?? []).filter(isGroup);
  for (const g of groups) {
    if (g.id !== id) add(g.id);
    for (const mem of ctx.kids.get(g.id) ?? []) add(mem.id);
  }
  const parent = node?.parentId ? ctx.byId.get(node.parentId) : undefined;
  if (isGroup(parent)) add(parent!.id);
}

/** Paketin seçime gireceği kalemler (katalogda olmayan — ör. kullanımdan kalkmış — kalem düşer). */
export function expandPackage(packageId: string, ctx: SelectionContext): Set<string> {
  const out = new Set<string>();
  for (const id of ctx.itemsByPackage.get(packageId) ?? []) {
    if (ctx.byId.has(id)) addWithBreakdown(id, ctx, out);
  }
  return out;
}

/** Sete ekle satırı: "N eksiği seç" — sette olmayan kalem sayısı (0 → "tamamı sette"). */
export function packageMissingCount(packageId: string, ctx: SelectionContext): number {
  return expandPackage(packageId, ctx).size;
}

/** Sete ekle satırı: "sette X/Y" (katalogdaki paket kalemleri). */
export function packageInSetCount(
  packageId: string,
  ctx: SelectionContext,
): { inSet: number; total: number } {
  const items = (ctx.itemsByPackage.get(packageId) ?? []).filter((id) => ctx.byId.has(id));
  return { inSet: items.filter((id) => ctx.inSet.has(id)).length, total: items.length };
}

export function packageUnion(checked: readonly string[], ctx: SelectionContext): Set<string> {
  const out = new Set<string>();
  for (const p of checked) for (const id of expandPackage(p, ctx)) out.add(id);
  return out;
}

function derive(
  checked: readonly string[],
  manualAdded: ReadonlySet<string>,
  manualRemoved: ReadonlySet<string>,
  ctx: SelectionContext,
): PackageSelectionState {
  const selected = new Set<string>();
  for (const id of packageUnion(checked, ctx)) if (!manualRemoved.has(id)) selected.add(id);
  for (const id of manualAdded) if (!ctx.inSet.has(id)) selected.add(id);
  return { checked, manualAdded, manualRemoved, selected };
}

/**
 * Paket listesi yeniden yüklendikten sonra (Yeniden denetle / sunucu reddi): artık etkin olmayan
 * paketler `checked`'ten düşer ve seçim yeni üyelikle yeniden türetilir (elle değişiklikler korunur).
 */
export function retainActivePackages(
  state: PackageSelectionState,
  activeIds: ReadonlySet<string>,
  ctx: SelectionContext,
): PackageSelectionState {
  const checked = state.checked.filter((id) => activeIds.has(id));
  return derive(checked, state.manualAdded, state.manualRemoved, ctx);
}

/** Paket aç/kapa: açınca kalemleri girer; kapatınca başka işaretli pakette ya da elle eklenmiş olan kalır. */
export function applyPackageToggle(
  state: PackageSelectionState,
  packageId: string,
  on: boolean,
  ctx: SelectionContext,
): PackageSelectionState {
  const has = state.checked.includes(packageId);
  if (on === has) return state;
  const checked = on ? [...state.checked, packageId] : state.checked.filter((p) => p !== packageId);
  return derive(checked, state.manualAdded, state.manualRemoved, ctx);
}

/** Katalog sekmesi değişimi: yeni seçimi elle ekleme / çıkarma olarak kaydeder. */
export function applyCatalogChange(
  state: PackageSelectionState,
  next: ReadonlySet<string>,
  ctx: SelectionContext,
): PackageSelectionState {
  const union = packageUnion(state.checked, ctx);
  const manualAdded = new Set(state.manualAdded);
  const manualRemoved = new Set(state.manualRemoved);
  for (const id of next) {
    if (state.selected.has(id)) continue;
    if (union.has(id)) manualRemoved.delete(id);
    else manualAdded.add(id);
  }
  for (const id of state.selected) {
    if (next.has(id)) continue;
    manualAdded.delete(id);
    if (union.has(id)) manualRemoved.add(id);
  }
  return derive(state.checked, manualAdded, manualRemoved, ctx);
}

function leavesBelow(id: string, ctx: SelectionContext, out: string[] = []): string[] {
  for (const k of ctx.kids.get(id) ?? []) {
    if ((ctx.kids.get(k.id) ?? []).length === 0) out.push(k.id);
    else leavesBelow(k.id, ctx, out);
  }
  return out;
}

/**
 * Fork ağacı yalnız yaprak işaretler: bu değişimle altındaki yaprakların TÜMÜ bırakılan yaprak
 * olmayan kalem (kırılımlı toplam, grup) da düşer. Hiç yaprağı seçili olmamış kalem korunur.
 */
export function pruneEmptiedBranches(
  prev: ReadonlySet<string>,
  next: ReadonlySet<string>,
  ctx: SelectionContext,
): Set<string> {
  const out = new Set(next);
  for (const id of next) {
    const leaves = leavesBelow(id, ctx);
    if (leaves.length === 0) continue;
    if (leaves.some((l) => prev.has(l)) && !leaves.some((l) => next.has(l))) out.delete(id);
  }
  return out;
}

export interface UnresolvedRef {
  metricId: string;
  code: string;
  missing: string[];
}

export interface SelectionSummary {
  total: number;
  perPackage: { id: string; name: string; count: number }[];
  /** birden çok pakette olup tek sayılan kalem */
  common: number;
  /** paket kaleminden elle çıkarılan */
  removed: number;
  /** paketlerin dışında elle eklenen */
  added: number;
  /** seçimde olmayan, otomatik eklenecek üst kategoriler */
  ancestors: number;
  /** üyeleriyle gelen kırılımlı toplam sayısı */
  breakdownMembers: number;
  /** paket birim varsayılanı ön seçili gösterge sayısı */
  unitPreselect: number;
  /** formül başvurusu seçimde (ve sette) olmayan türetilmiş göstergeler */
  unresolvedRefs: UnresolvedRef[];
}

function formulaRefs(m: SelectionMetric): string[] {
  const out = new Set<string>();
  for (const f of m.defaultFormulas) {
    const p = parseFormula(f.expression);
    if (p.ok) for (const r of p.refs) out.add(r);
  }
  return [...out];
}

/** Sunucu `unresolvedFormulaRefs` eşi: başvuru seçili/sette ya da altında seçili bir düğüm varsa çözülür. */
function unresolvedRefsOf(selected: ReadonlySet<string>, ctx: SelectionContext): UnresolvedRef[] {
  const resolved = (id: string): boolean => selected.has(id) || ctx.inSet.has(id);
  const anyBelow = (id: string, seen = new Set<string>()): boolean => {
    if (seen.has(id)) return false;
    seen.add(id);
    return (ctx.kids.get(id) ?? []).some((c) => resolved(c.id) || anyBelow(c.id, seen));
  };
  const byCode = new Map(ctx.catalog.map((c) => [c.code, c]));
  const out: UnresolvedRef[] = [];
  for (const m of ctx.catalog) {
    if (!selected.has(m.id) || m.defaultFormulas.length === 0) continue;
    const missing = formulaRefs(m)
      .filter((code) => {
        const dep = byCode.get(code);
        if (!dep || dep.id === m.id) return false;
        return !resolved(dep.id) && !anyBelow(dep.id);
      })
      .sort();
    if (missing.length > 0) out.push({ metricId: m.id, code: m.code, missing });
  }
  return out;
}

export function summarize(
  state: PackageSelectionState,
  ctx: SelectionContext,
  opts: { packages: readonly KpiPackage[]; unitDefaults?: ReadonlyMap<string, string> },
): SelectionSummary {
  const { selected } = state;
  const checked = new Set(state.checked);
  const union = packageUnion(state.checked, ctx);
  const perPackage = opts.packages
    .filter((p) => checked.has(p.id))
    .map((p) => {
      let count = 0;
      for (const id of expandPackage(p.id, ctx)) if (selected.has(id)) count += 1;
      return { id: p.id, name: p.name, count };
    });
  let unionSelected = 0;
  let removed = 0;
  for (const id of union) {
    if (selected.has(id)) unionSelected += 1;
    else removed += 1;
  }
  let added = 0;
  for (const id of selected) if (!union.has(id)) added += 1;

  const ancestors = new Set<string>();
  const breakdownTotals = new Set<string>();
  for (const id of selected) {
    const node = ctx.byId.get(id);
    const parent = node?.parentId ? ctx.byId.get(node.parentId) : undefined;
    if (isGroup(parent) && parent!.parentId) breakdownTotals.add(parent!.parentId);
    const seen = new Set<string>();
    let cur = parent;
    while (cur && !seen.has(cur.id)) {
      seen.add(cur.id);
      if (!selected.has(cur.id) && !ctx.inSet.has(cur.id)) ancestors.add(cur.id);
      cur = cur.parentId ? ctx.byId.get(cur.parentId) : undefined;
    }
  }
  let unitPreselect = 0;
  if (opts.unitDefaults)
    for (const id of opts.unitDefaults.keys()) if (selected.has(id)) unitPreselect += 1;

  return {
    total: selected.size,
    perPackage,
    // listede olmayan işaretli paket sayılmaz → fark eksiye düşmesin
    common: Math.max(0, perPackage.reduce((n, p) => n + p.count, 0) - unionSelected),
    removed,
    added,
    ancestors: ancestors.size,
    breakdownMembers: breakdownTotals.size,
    unitPreselect,
    unresolvedRefs: unresolvedRefsOf(selected, ctx),
  };
}

export type PackageSourceTab = "package" | "catalog";

/**
 * DR2 açılış: etkin paket yoksa (liste hatası dahil — `packages: null`) Katalog sekmesi + tümü
 * işaretli (byte-eşit eski davranış); varsa Paket sekmesi + BOŞ seçim. Tek istisna: Müşteri kaydı
 * ipucundan (`initialPackageId`, DR21) o paket işaretli açılır.
 */
export function forkOpening(a: {
  leafIds: readonly string[];
  packages: readonly KpiPackage[] | null;
  ctx: SelectionContext;
  initialPackageId?: string | null | undefined;
}): { tab: PackageSourceTab; state: PackageSelectionState } {
  const active = (a.packages ?? []).filter((p) => p.status === "active");
  if (active.length === 0) return { tab: "catalog", state: catalogSelection(a.leafIds) };
  const hint = a.initialPackageId && active.some((p) => p.id === a.initialPackageId);
  const state = hint
    ? applyPackageToggle(emptySelection(), a.initialPackageId!, true, a.ctx)
    : emptySelection();
  return { tab: "package", state };
}

/** Önerilen (müşteri sektörüyle eşleşen) paketler üstte; aksi sunucu sırası korunur. */
export function sortSuggestedFirst(
  packages: readonly KpiPackage[],
  sectorCode: string | null,
): { list: KpiPackage[]; suggested: Set<string> } {
  const active = packages.filter((p) => p.status === "active");
  const suggested = new Set(
    sectorCode ? active.filter((p) => p.sectorCode === sectorCode).map((p) => p.id) : [],
  );
  return {
    list: [
      ...active.filter((p) => suggested.has(p.id)),
      ...active.filter((p) => !suggested.has(p.id)),
    ],
    suggested,
  };
}

/**
 * ≤720 px Set kur: birincil düğme (İleri / Oluştur) özetle birlikte yapışkan alt şeritte durur
 * (gösterge sayısı ve eylem aynı yerde); alt bilgide yalnız İptal/Geri kalır. Birim adımında özet
 * kartı yok → düğme alt bilgide. Geniş ekranda her zaman alt bilgi.
 */
export function forkPrimaryPlacement(narrow: boolean, onUnitsStep: boolean): "strip" | "footer" {
  return narrow && !onUnitsStep ? "strip" : "footer";
}
